import moment from "moment";

const DISPLAY_FORMAT = "MM/DD/YYYY";
const DISPLAY_DATETIME_FORMAT = "M/D/YYYY h:mm A";
const CALENDAR_INPUT_FORMATS = ["YYYY-MM-DD", "MM/DD/YYYY", "M/D/YYYY"];

/**
 * Format a TIMESTAMPTZ value (e.g. pharmacy_tasks.created_at) for display.
 *
 * The API serializes it with an explicit offset, so a local parse renders the
 * viewer's correct calendar day AND stays aligned with the Date-of-Service filter
 * (which builds its bounds in local time). Do NOT force UTC here.
 *
 * @param value - ISO-8601 timestamp string with offset, or null
 * @returns MM/DD/YYYY in the viewer's local time, the raw value if unparseable, or "--"
 */
export const formatServiceDate = (value: string | null): string => {
  if (!value) return "--";
  const parsed = moment(value);
  return parsed.isValid() ? parsed.format(DISPLAY_FORMAT) : value;
};

/**
 * Format a message timestamp (e.g. a thread message's sent_at) for chat display.
 *
 * Like formatServiceDate, the value carries an explicit offset, so parse locally to
 * render the viewer's correct time of day. Returns "M/D/YYYY h:mm A" (e.g.
 * "9/15/2026 2:55 AM"), an empty string when there is no timestamp (pre-existing
 * messages), or the raw value if unparseable.
 *
 * @param value - ISO-8601 timestamp string with offset, or null
 * @returns "M/D/YYYY h:mm A" in the viewer's local time, "" if empty, raw value if unparseable
 */
export const formatMessageTimestamp = (
  value: string | null | undefined,
): string => {
  if (!value) return "";
  const parsed = moment(value);
  return parsed.isValid() ? parsed.format(DISPLAY_DATETIME_FORMAT) : value;
};

/**
 * Format a plain calendar date (e.g. patient_dob, a free-text VARCHAR) for display.
 *
 * A date of birth has no timezone: parse it strictly against the known formats and
 * never route it through UTC, or a non-ISO value like "12/12/1966" shifts a day back
 * for positive-offset viewers (IST, CET, ...).
 *
 * Anything that doesn't match a listed format is returned verbatim (not coerced) --
 * including legal partial FHIR dates like "1966" or "1966-05". This is deliberate:
 * rendering "1966" as "01/01/1966" would fabricate a day/month on a DOB, so we show
 * the partial value as-is instead.
 *
 * @param value - Calendar date string (YYYY-MM-DD, MM/DD/YYYY, M/D/YYYY), or null
 * @returns MM/DD/YYYY, the raw value if it matches no known format, or "--"
 */
export const formatCalendarDate = (value: string | null): string => {
  if (!value) return "--";
  const parsed = moment(value, CALENDAR_INPUT_FORMATS, true);
  return parsed.isValid() ? parsed.format(DISPLAY_FORMAT) : value;
};

/**
 * Formats a Date object to YYYYMMDD string format
 * @param date - Date object to format
 * @returns Formatted date string in YYYYMMDD format, or empty string if date is null
 *
 * @example
 * formatDateToYYYYMMDD(new Date(2025, 11, 15)) // Returns "20251215"
 * formatDateToYYYYMMDD(null) // Returns ""
 */
export const formatDateToYYYYMMDD = (date: Date | null): string => {
  if (!date) return "";
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");
  return `${year}${month}${day}`;
};
